import { fromPostgrest, guard, raise, type Result } from '@/lib/result';
import { supabase } from '@/lib/supabase';

import { mapProfile, toProfileRow, type Profile, type ProfileWriteInput } from '../model/profile';

export const profileRepository = {
  /** Phase 3: record granular PDPL consent for location use (check-in). */
  async giveLocationConsent(): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.rpc('give_location_consent');
      if (error) raise('server', error.message, error);
    });
  },

  async getById(id: string): Promise<Result<Profile | null>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw fromPostgrest(error);
      return data ? mapProfile(data as unknown) : null;
    });
  },

  async upsert(input: ProfileWriteInput): Promise<Result<Profile>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('profiles')
        .upsert(toProfileRow(input))
        .select('*')
        .single();
      if (error) throw fromPostgrest(error);
      return mapProfile(data as unknown);
    });
  },

  /** Phase 4 (PDPL): the caller's full data export as JSON. */
  async exportMyData(): Promise<Result<unknown>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('export_my_data');
      if (error) throw fromPostgrest(error);
      return data;
    });
  },

  /** Phase 4 (PDPL): mark the account for deletion (retention job anonymizes @30d). */
  async requestDeletion(): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.rpc('request_account_deletion');
      if (error) raise('server', error.message, error);
    });
  },

  /** Providers (imam/muezzin) for the admin verification panel, newest first. */
  async listProviders(): Promise<Result<readonly Profile[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .in('role', ['imam', 'muezzin'])
        .order('created_at', { ascending: false });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapProfile);
    });
  },

  /**
   * Phase 5 (D-013): every profile for the admin user browser. RLS on `profiles`
   * is already app-wide-authenticated-read (Phase 1), so no policy change; the
   * caller-side gate is the admin UI itself.
   */
  async listAllProfiles(): Promise<Result<readonly Profile[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapProfile);
    });
  },
};
